import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  FaSignature,
  FaUpload,
  FaDownload,
  FaFileImage,
  FaFilePdf,
  FaFileAlt,
  FaCheck,
  FaTimes,
  FaArrowRight,
  FaArrowLeft,
  FaSearchPlus,
  FaSearchMinus,
  FaLock,
  FaRedo
} from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import {
  fetchDocRequests,
  uploadSignedDocument,
  uploadDocumentFile,
  updateDocRequestStatus,
  addRecentActivity
} from '../../api/profile';
import LoadingSpinner from '../common/Loader';
import Modal from 'react-modal';
import { saveAs } from 'file-saver';
import { PDFDocument, rgb } from 'pdf-lib';

Modal.setAppElement('#root');

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: '1200px',
    height: '90vh',
    padding: 0,
    overflow: 'hidden',
    zIndex: 2000,
  },
  overlay: {
    zIndex: 2000,
    backgroundColor: 'rgba(0,0,0,0.5)'
  }
};

const DocumentRequests = () => {
  const { user } = useContext(AuthContext);
  const [docRequests, setDocRequests] = useState([]);
  const [signatureDocs, setSignatureDocs] = useState([]);
  const [uploadDocs, setUploadDocs] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [errorRequests, setErrorRequests] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentFlatDocIndex, setCurrentFlatDocIndex] = useState(0);
  const [flatSignatureDocs, setFlatSignatureDocs] = useState([]);

  const [isUploading, setIsUploading] = useState(false);
  const [filledPdfs, setFilledPdfs] = useState({});
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [isSigned, setIsSigned] = useState({});
  const iframeRef = useRef(null);

  const [signatureValues, setSignatureValues] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const refreshData = async () => {
    try {
      setLoadingRequests(true);
      const requests = await fetchDocRequests(user.user_id, user.access_token);
      setDocRequests(requests);

      const signatureRequests = requests.filter(
        req => req.requestType === 'signature' && req.status === 'pending'
      );
      setSignatureDocs(signatureRequests);

      const uploadRequests = requests.filter(
        req => req.requestType === 'upload' && req.status === 'pending'
      );
      setUploadDocs(uploadRequests);

      const flatDocs = [];
      signatureRequests.forEach(doc => {
        doc.templatePaths.forEach((template, idx) => {
          flatDocs.push({
            parentDocId: doc.id,
            docTitle: doc.title,
            templatePath: template,
            templateIndex: idx,
            fileName: template.originalPath.split('/').pop() || 'document.pdf'
          });
        });
      });
      setFlatSignatureDocs(flatDocs);
      setCurrentFlatDocIndex(0);
      setNeedsRefresh(false);
    } catch (err) {
      // Suppress error if 404 Document Requests not found or "Failed to load document requests"
      if (
        (err.status === 404 && err.message === 'Document Requests not found') ||
        err.message === null
      ) {
        setErrorRequests(null);
        setDocRequests([]);
        setSignatureDocs([]);
        setUploadDocs([]);
        setFlatSignatureDocs([]);
      } else {
        setErrorRequests(err.message || 'Failed to load document requests');
      }
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleUpload = (requestId) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.jpg,.jpeg,.png,.gif';
    fileInput.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('userId', user.user_id);

        await uploadDocumentFile(formData, user.user_id, user.access_token);
        await refreshData();

        await addRecentActivity({
          userId: user.user_id,
          description: 'Document uploaded'
        });

        setSuccessMessage('Document uploaded successfully');
      } catch (error) {
        console.error('[ERROR] Document upload failed:', error);
        alert('Failed to upload document: ' + (error.message || error));
      } finally {
        setIsUploading(false);
      }
    };
    fileInput.click();
  };

  useEffect(() => {
    refreshData();
  }, [user.user_id, user.access_token]);

  useEffect(() => {
    if (isModalOpen && flatSignatureDocs.length > 0) {
      const currentDoc = flatSignatureDocs[currentFlatDocIndex];
      if (currentDoc) {
        setPreviewUrl(currentDoc.templatePath.signedUrl);
      }
    }
  }, [currentFlatDocIndex, flatSignatureDocs, isModalOpen]);

  useEffect(() => {
    if (needsRefresh) {
      refreshData();
    }
  }, [needsRefresh]);

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
      const blob = await response.blob();
      saveAs(blob, filename);
    } catch (err) {
      console.error("[ERROR] Download failed:", err);
      alert('Failed to download document');
    }
  };

  const fillAndFlattenPdf = async (pdfUrl, signature) => {
    try {
      setIsLoadingPdf(true);
      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const form = pdfDoc.getForm();

      // Try to find existing signature field
      let signatureField = null;
      const signatureFieldNames = ['signature', 'signature_field', 'sign', 'signer', 'name'];

      for (const name of signatureFieldNames) {
        try {
          signatureField = form.getTextField(name);
          break;
        } catch (e) { }
      }

      // Add signature to last page if no field found
      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];
      const { width, height } = lastPage.getSize();

      if (!signatureField) {
        signatureField = form.createTextField('signature_field');
        signatureField.addToPage(lastPage, {
          x: width - 200,
          y: 30,
          width: 180,
          height: 20,
          fontSize: 10,
          borderColor: rgb(0, 0, 0),
          borderWidth: 0.5,
        });
      }

      signatureField.setText(signature);
      form.flatten();

      const pdfBytes = await pdfDoc.save();
      setIsLoadingPdf(false);
      return new Blob([pdfBytes], { type: 'application/pdf' });
    } catch (error) {
      setIsLoadingPdf(false);
      console.error('[ERROR] Error filling and flattening PDF:', error);
      throw new Error('Failed to fill and flatten PDF: ' + error.message);
    }
  };

  const handleSubmitSignatures = async () => {
    setIsUploading(true);
    try {
      const newFilledPdfs = { ...filledPdfs };

      // Validate all signatures first
      for (let i = 0; i < flatSignatureDocs.length; i++) {
        const doc = flatSignatureDocs[i];
        const parentId = doc.parentDocId;
        const signature = signatureValues[parentId] || '';

        if (!signature.trim()) {
          alert(`Please enter a signature for document: ${doc.docTitle}`);
          setIsUploading(false);
          return;
        }
      }

      // Process and sign each document
      for (let i = 0; i < flatSignatureDocs.length; i++) {
        const doc = flatSignatureDocs[i];
        const parentId = doc.parentDocId;

        if (!isSigned[parentId]) {
          const flattenedBlob = await fillAndFlattenPdf(
            doc.templatePath.signedUrl,
            signatureValues[parentId]
          );

          if (!newFilledPdfs[parentId]) {
            newFilledPdfs[parentId] = [];
          }
          newFilledPdfs[parentId][doc.templateIndex] = flattenedBlob;
        }
      }

      setFilledPdfs(newFilledPdfs);

      // Upload all signed documents
      for (const parentId in newFilledPdfs) {
        if (newFilledPdfs[parentId]) {
          const parentDoc = signatureDocs.find(d => d.id === parentId);
          const files = newFilledPdfs[parentId].map((blob, idx) => {
            const templatePath = flatSignatureDocs.find(
              doc => doc.parentDocId === parentId && doc.templateIndex === idx
            );
            const originalFileName = templatePath?.fileName || `document_${idx + 1}.pdf`;
            const fileName = `signed_${originalFileName.replace('.pdf', '')}.pdf`;
            return new File([blob], fileName, { type: 'application/pdf' });
          });

          const formData = new FormData();
          files.forEach(file => formData.append('files', file));

          await uploadSignedDocument(parentId, formData, user.access_token);
          await updateDocRequestStatus(parentId, user.user_id);

          await addRecentActivity({
            userId: user.user_id,
            description: `Document signed: ${parentDoc?.title || parentId}`
          });

          setIsSigned(prev => ({ ...prev, [parentId]: true }));
        }
      }

      setIsModalOpen(false);
      setFilledPdfs({});
      setSignatureValues({});
      setSuccessMessage('All documents signed and submitted successfully!');
      setNeedsRefresh(true);
    } catch (error) {
      console.error('[ERROR] Signature submission failed:', error);
      alert(`Failed to submit signatures: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNextDoc = () => {
    if (currentFlatDocIndex < flatSignatureDocs.length - 1) {
      setCurrentFlatDocIndex(currentFlatDocIndex + 1);
    }
  };

  const handlePrevDoc = () => {
    if (currentFlatDocIndex > 0) {
      setCurrentFlatDocIndex(currentFlatDocIndex - 1);
    }
  };

  const handleSignatureStart = () => {
    if (flatSignatureDocs.length > 0) {
      setCurrentFlatDocIndex(0);
      setFilledPdfs({});
      setIsSigned({});
      setSignatureValues({});
      setIsModalOpen(true);
    } else {
      alert('No signature documents available to sign.');
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2.0));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleSignatureChange = (docId, value) => {
    setSignatureValues(prev => ({
      ...prev,
      [docId]: value,
    }));
  };

  const getFileIcon = (filename) => {
    const extension = filename?.split('.').pop().toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FaFileImage className="text-red-500" />;
      case 'pdf':
        return <FaFilePdf className="text-red-500" />;
      default:
        return <FaFileAlt className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileNameFromPath = (path) => {
    if (!path) return 'document';
    const decodedPath = decodeURIComponent(path);
    return decodedPath.split('/').pop().replace(/%20/g, ' ');
  };

  const hasSignatureRequests = signatureDocs.length > 0;
  const hasUploadRequests = uploadDocs.length > 0;
  const hasRegularRequests = docRequests.filter(
    req => req.status === 'pending' && req.requestType !== 'signature' && req.requestType !== 'upload'
  ).length > 0;
  const regularRequests = docRequests.filter(
    req => req.status === 'pending' && req.requestType !== 'signature' && req.requestType !== 'upload'
  );

  if (loadingRequests) return <LoadingSpinner />;

  if (errorRequests) {
    // Suppress error display for 404 or "Failed to load document requests"
    if (
      errorRequests === 'Document Requests not found' ||
      errorRequests === 'Failed to load document requests'
    ) {
      return null;
    }
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        {errorRequests}
      </div>
    );
  }

  return (
    <div className="p-4">
      <section className="mb-8">
        {hasSignatureRequests && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Signature Requests</h2>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-red-800 flex items-center">
                  <FaSignature className="mr-2" />
                  Signature Required ({signatureDocs.reduce((acc, doc) => acc + (doc.templatePaths?.length || 0), 0)} documents)
                </h3>
                <p className="text-sm text-red-600 mt-1">
                  You have documents that require your electronic signature.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSignatureStart}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                disabled={isUploading}
              >
                <FaSignature className="mr-2" /> Start Signing
              </button>
            </div>
          </div>
        )}

        {hasUploadRequests && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Upload Requests</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Urgent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {uploadDocs.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{req.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">{req.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(req.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {req.isUrgent === 1 ? (
                          <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">Urgent</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">Normal</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {req.docType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleUpload(req.id)}
                          className="flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          disabled={isUploading}
                        >
                          <FaUpload className="mr-1" /> Upload
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {hasRegularRequests && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Urgent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {regularRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{req.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">{req.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(req.dueDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {req.isUrgent === 1 ? (
                        <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">Urgent</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">Normal</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleUpload(req.id)}
                        className="flex items-center px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                        disabled={isUploading}
                      >
                        <FaUpload className="mr-1" /> Upload
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {successMessage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h2 className="text-xl font-bold mb-4 text-primary">Success!</h2>
            <p className="mb-4">{successMessage}</p>
            <button
              className="w-full bg-primary text-white p-2 rounded hover:bg-primary transition flex items-center justify-center"
              onClick={() => {
                setSuccessMessage(null);
                refreshData();
              }}
            >
              <FaRedo className="mr-2" /> Refresh
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Document Signing"
        style={customStyles}
        ariaHideApp={false}
        onAfterClose={() => {
          setPreviewUrl('');
          setZoomLevel(1.0);
          Object.values(filledPdfs).forEach(blobArray => {
            blobArray.forEach(blob => URL.revokeObjectURL(blob));
          });
        }}
      >
        {flatSignatureDocs.length > 0 && flatSignatureDocs[currentFlatDocIndex] && (
          <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col h-full">
            <div className="flex justify-between items-center border-b p-4 bg-gray-50">
              <div>
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <FaSignature className="inline mr-2" />
                  Sign Document: {flatSignatureDocs[currentFlatDocIndex].fileName}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  File {currentFlatDocIndex + 1} of {flatSignatureDocs.length}
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full"
                aria-label="Close"
                disabled={isUploading}
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-auto p-4 bg-gray-100 min-h-0">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-700">
                    {flatSignatureDocs[currentFlatDocIndex].fileName}
                    {isSigned[flatSignatureDocs[currentFlatDocIndex].parentDocId] && (
                      <span className="ml-2 text-green-600 flex items-center text-sm">
                        <FaLock className="mr-1" /> Signed and Locked
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleZoomOut}
                      className="p-1 text-gray-600 hover:text-red-600 disabled:text-gray-300"
                      disabled={zoomLevel <= 0.5 || isUploading}
                      aria-label="Zoom out"
                    >
                      <FaSearchMinus size={18} />
                    </button>
                    <span className="px-2 py-1 text-sm bg-white rounded border">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    <button
                      onClick={handleZoomIn}
                      className="p-1 text-gray-600 hover:text-red-600 disabled:text-gray-300"
                      disabled={zoomLevel >= 2.0 || isUploading}
                      aria-label="Zoom in"
                    >
                      <FaSearchPlus size={18} />
                    </button>
                  </div>
                </div>

                <div className="border border-gray-300 bg-white shadow-sm h-full overflow-auto">
                  {isLoadingPdf ? (
                    <div className="flex justify-center items-center h-full">
                      <LoadingSpinner />
                      <span className="ml-2">Processing document...</span>
                    </div>
                  ) : (
                    <iframe
                      ref={iframeRef}
                      src={previewUrl}
                      title={`Document Preview - ${flatSignatureDocs[currentFlatDocIndex].fileName}`}
                      className="w-full h-full"
                      style={{
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: '0 0',
                        width: `${100 / zoomLevel}%`,
                        height: `${100 / zoomLevel}%`
                      }}
                      frameBorder="0"
                    />
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-white">
                <label htmlFor="signature-input" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter your full name as your electronic signature
                  <span className="text-xs text-gray-500 block mt-1">
                    Your signature will be placed at the bottom of the last page.
                  </span>
                </label>
                <input
                  type="text"
                  id="signature-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  value={signatureValues[flatSignatureDocs[currentFlatDocIndex].parentDocId] || ''}
                  onChange={(e) => handleSignatureChange(
                    flatSignatureDocs[currentFlatDocIndex].parentDocId,
                    e.target.value
                  )}
                  disabled={isUploading || isLoadingPdf || isSigned[flatSignatureDocs[currentFlatDocIndex].parentDocId]}
                  placeholder="Type your full name"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  {currentFlatDocIndex > 0 && (
                    <button
                      onClick={handlePrevDoc}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center"
                      disabled={isUploading || isLoadingPdf}
                    >
                      <FaArrowLeft className="mr-2" /> Previous
                    </button>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      const currentDoc = flatSignatureDocs[currentFlatDocIndex];
                      const parentSigned = isSigned[currentDoc.parentDocId];
                      const url = parentSigned
                        ? URL.createObjectURL(filledPdfs[currentDoc.parentDocId][currentDoc.templateIndex])
                        : currentDoc.templatePath.signedUrl;
                      handleDownload(url, currentDoc.fileName);
                    }}
                    className="px-3 py-2 bg-primary text-white rounded hover:bg-primary flex items-center text-sm"
                    disabled={isUploading || isLoadingPdf}
                  >
                    <FaDownload className="mr-1" /> Download
                  </button>
                </div>

                <div>
                  {currentFlatDocIndex < flatSignatureDocs.length - 1 ? (
                    <button
                      onClick={handleNextDoc}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                      disabled={isUploading || isLoadingPdf}
                    >
                      Next <FaArrowRight className="ml-2" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmitSignatures}
                      className="px-4 py-2 bg-primary text-white rounded hover:bg-primary flex items-center justify-center min-w-[150px] h-[42px]"
                      disabled={isUploading || isLoadingPdf}
                    >
                      {isUploading ? (
                        <span className="flex items-center">
                          <LoadingSpinner small white className="mr-2" />
                          Submitting...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          Submit All <FaCheck className="ml-2" />
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DocumentRequests;